import { ThemeSwitcher } from '@pwa/components/theme-switcher';
import { orgQueries } from '@pwa/queries/org/org';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/tabs';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Bell, Lock, Palette, ShieldAlert, User } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_dashboard/settings')({
	validateSearch: (search: Record<string, unknown>): { tab: SettingsTab } => {
		const tab = search.tab;
		return {
			tab: tab === 'appearance' || tab === 'notifications' || tab === 'account' ? tab : 'account',
		};
	},
	component: RouteComponent,
});

type SettingsTab = 'appearance' | 'account' | 'notifications';

const TABS: ReadonlyArray<{ value: SettingsTab; label: string; icon: typeof Palette }> = [
	{ value: 'appearance', label: 'Theme', icon: Palette },
	{ value: 'account', label: 'Account', icon: User },
	{ value: 'notifications', label: 'Notifications', icon: Bell },
];

function RouteComponent() {
	const navigate = useNavigate();
	const { tab: activeTab } = Route.useSearch();
	const { data: orgList } = useQuery(orgQueries.list());
	const selectedOrg = orgList?.organizations[0] ?? null;

	useEffect(() => {
		document.title = 'Account settings · Scout';
	}, []);

	return (
		<div className='container mx-auto max-w-2xl p-6 space-y-8'>
			{selectedOrg ? (
				<Link
					to='/org/$orgId'
					params={{ orgId: selectedOrg.id }}
					className='group inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground'
					data-testid='account-settings-back'
				>
					<ArrowLeft
						className='size-3 transition-transform duration-200 group-hover:-translate-x-0.5'
						aria-hidden='true'
					/>
					Back to workspace
				</Link>
			) : (
				<Link
					to='/'
					className='group inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground'
					data-testid='account-settings-back'
				>
					<ArrowLeft
						className='size-3 transition-transform duration-200 group-hover:-translate-x-0.5'
						aria-hidden='true'
					/>
					Back to workspace
				</Link>
			)}

			<header className='space-y-2'>
				<h1 className='text-3xl font-bold tracking-tight'>Account settings</h1>
				<p className='text-muted-foreground'>Manage your account, security, and personal preferences.</p>
			</header>
			<Tabs
				value={activeTab}
				onValueChange={(val) =>
					navigate({
						to: '/settings',
						search: { tab: val as SettingsTab },
						replace: true,
					})
				}
				className='space-y-6 container w-full'
			>
				<TabsList variant='line'>
					{TABS.map((tab) => {
						const Icon = tab.icon;
						return (
							<TabsTrigger
								key={tab.value}
								value={tab.value}
								id={`settings-tab-${tab.value}`}
								className='px-3 py-2.5 text-sm font-medium whitespace-nowrap shrink-0 rounded-none bg-transparent hover:text-foreground after:bg-primary'
							>
								<Icon className='size-4' aria-hidden='true' />
								{tab.label}
							</TabsTrigger>
						);
					})}
				</TabsList>

				{/* Appearance panel */}
				<TabsContent value='appearance' id='settings-panel-appearance' className='space-y-6 outline-none'>
					<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300 shadow-sm'>
						<CardHeader>
							<div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
								<div className='space-y-1.5'>
									<CardTitle>Look and feel</CardTitle>
									<CardDescription>Choose how Scout looks: light, dark, or match your system.</CardDescription>
								</div>
								<div className='shrink-0 self-start sm:self-auto'>
									<ThemeSwitcher />
								</div>
							</div>
						</CardHeader>
						<CardContent className='text-sm text-muted-foreground'>
							<p>
								Pick a theme that works for you. Your selection is remembered across devices signed in to the same
								account.
							</p>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Account panel */}
				<TabsContent value='account' id='settings-panel-account' className='space-y-6 outline-none'>
					<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300 shadow-sm'>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<User className='size-4 text-muted-foreground' aria-hidden='true' />
								Profile
							</CardTitle>
							<CardDescription>Account settings coming soon.</CardDescription>
						</CardHeader>
						<CardContent>
							<div className='flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center'>
								<div className='rounded-full bg-muted p-3 text-muted-foreground'>
									<User className='size-5' aria-hidden='true' />
								</div>
								<div className='space-y-1'>
									<p className='text-sm font-medium'>Profile editing coming soon</p>
									<p className='text-xs text-muted-foreground max-w-sm'>
										We&apos;re working on profile editing, password management, and connected sessions.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300 delay-60 fill-mode-backwards shadow-sm'>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<Lock className='size-4 text-muted-foreground' aria-hidden='true' />
								Security
							</CardTitle>
							<CardDescription>Two-factor authentication and active sessions.</CardDescription>
						</CardHeader>
						<CardContent>
							<p className='text-xs text-muted-foreground'>Two-factor setup and session management will land here.</p>
						</CardContent>
					</Card>

					<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300 delay-120 fill-mode-backwards border-destructive/40 shadow-sm'>
						<CardHeader>
							<CardTitle className='flex items-center gap-2 text-destructive'>
								<ShieldAlert className='size-4' aria-hidden='true' />
								Danger zone
							</CardTitle>
							<CardDescription>Careful. These actions can&apos;t be undone.</CardDescription>
						</CardHeader>
						<CardContent>
							<div className='flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between'>
								<div className='space-y-1'>
									<p className='text-sm font-medium'>Delete account</p>
									<p className='text-xs text-muted-foreground'>
										Permanently remove your account, workspaces, and all associated data.
									</p>
								</div>
								<Button
									variant='destructive'
									size='sm'
									disabled
									className='self-start bg-destructive text-white hover:bg-destructive/90 sm:self-auto dark:bg-destructive dark:hover:bg-destructive/90'
								>
									Delete account
								</Button>
							</div>
							<p className='mt-3 text-[11px] text-muted-foreground'>Account deletion is coming soon.</p>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Notifications panel */}
				<TabsContent value='notifications' id='settings-panel-notifications' className='space-y-6 outline-none'>
					<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300 shadow-sm'>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<Bell className='size-4 text-muted-foreground' aria-hidden='true' />
								Email notifications
							</CardTitle>
							<CardDescription>Choose which emails Scout sends you.</CardDescription>
						</CardHeader>
						<CardContent>
							<div className='flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center'>
								<div className='rounded-full bg-muted p-3 text-muted-foreground'>
									<Bell className='size-5' aria-hidden='true' />
								</div>
								<div className='space-y-1'>
									<p className='text-sm font-medium'>Notification settings coming soon</p>
									<p className='text-xs text-muted-foreground max-w-sm'>
										Granular email controls for invitations, mentions, and weekly digests.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
}
